import { ReactFragment } from "react";

const supportedLocales: Record<string, string> = {
    "zh-CN": "Chinese - Simplified",
    "zh-TW": "Chinese - Traditional",
    "cs-CZ": "Czech",
    "nl-NL": "Dutch",
    "en-GB": "English",
    "en-US": "English - US",
    "en-CA": "English – Canada",
    "en-AU": "English – Australia",
    "fr-FR": "French",
    "fr-CA": "French – Canada",
    "de-DE": "German",
    "it-IT": "Italian",
    "ja-JP": "Japanese",
    "ko-KR": "Korean",
    "pl-PL": "Polish",
    "pt-BR": "Portuguese – Brazil",
    "ru-RU": "Russian",
    "es-ES": "Spanish",
    "es-MX": "Spanish - LATAM",
    "tr-TR": "Turkish"
}

const GetOutageTitlebarLocale = (locale: string) => {
    var result = "Outage";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "zh-CN": "服务中断",
        "zh-TW": "停機",
        "nl-NL": "Onderbrekingen",
        "fr-FR": "Interruption",
        "fr-CA": "Interruption",
        "de-DE": "Ausfall",
        "it-IT": "Inattività",
        "ja-JP": "停止",
        "pl-PL": "Awaria",
        "pt-BR": "Falha geral",
        "ru-RU": "Приостановка работы",
        "es-ES": "Sin servicio",
    };
    return mapping[locale] ?? result;
};

const GetLiveStatusLocale = (locale: string) => {
    var resultStatus = "Live Status";
    var resultRefreshing = "Refreshing in XYZ seconds";

    var mappingLiveStatus: Record<string, string> = {
        "en-US": resultStatus,
        "en-GB": resultStatus,
        "en-CA": resultStatus,
        "en-AU": resultStatus,
        "pt-BR": "Status ao vivo",
        "cs-CZ": "Stav Live",
        "nl-NL": "Livestatus",
        "fr-FR": "État du live",
        "fr-CA": "État du live",
        "de-DE": "Live-Status",
        "it-IT": "Stato live",
        "ja-JP": "ライブステータス",
        "ko-KR": "라이브 현황",
        "es-MX": "Estado Live",
        "pl-PL": "Status działania",
        "ru-RU": "В сети",
        "zh-CN": "即时状态",
        "es-ES": "Estado online",
        "zh-TW": "即時狀態",
        "tr-TR": "Canlı Durum",
    };
    var mappingRefreshing: Record<string, string> = {
        "en-US": resultRefreshing,
        "en-GB": resultRefreshing,
        "en-CA": resultRefreshing,
        "en-AU": resultRefreshing,
        "fr-FR": "Rafraîchissement dans XYZ secondes",
        "fr-CA": "Rafraîchissement dans XYZ secondes",
        "es-ES": "Se actualizará en XYZ segundos",
        "de-DE": "Wird in XYZ Sekunden neu geladen",
        "it-IT": "Aggiornamento in XYZ secondi",
        "es-MX": "Actualizando en XYZ segundos",
        "pt-BR": "Atualiza em XYZ segundos",
        "nl-NL": "Ververst over XYZ seconden",
        "ru-RU": "Сброс через XYZ сек",
        "pl-PL": "Autoodświeżanie za XYZ sekund",
        "ja-JP": "XYZ 秒後に更新",
        "ar-AE": "التحديث خلال XYZ من الثواني",
        "zh-TW": "將在 XYZ 秒後重整……",
        "ko-KR": "XYZ 초 후 초기화",
        "cs-CZ": "Obnovení za XYZ sekund…",
        "tr-TR": "XYZ saniye sonra yenilenecek…",
        "zh-CN": "将在 XYZ 秒后重整……",
    };
    return (mappingLiveStatus[locale] ?? resultStatus) + " - " + (mappingRefreshing[locale] ?? resultRefreshing);
};

const GetServiceStatusLocale = (locale: string) => {
    var result = "Service Status";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Status do serviço",
        "cs-CZ": "Stav Služby",
        "nl-NL": "Servicestatus",
        "fr-FR": "État des services",
        "fr-CA": "État des services",
        "de-DE": "Dienst-Status",
        "it-IT": "Stato del servizio",
        "ja-JP": "サービスステータス",
        "ko-KR": "서비스 현황",
        "es-MX": "Estado de Servicio",
        "pl-PL": "Status służby",
        "ru-RU": "Тех. работы",
        "zh-CN": "服务状态",
        "es-ES": "Estado del servicio",
        "zh-TW": "服務狀態",
        "tr-TR": "Hizmet Durumu",
    };
    return mapping[locale] ?? result;
};

const GetNeedHelpLocale = (locale: string) => {
    var result = "Need Help?";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Precisa de ajuda?",
        "cs-CZ": "Potřebujete pomoc?",
        "nl-NL": "Hulp nodig?",
        "fr-FR": "Besoin d'aide ?",
        "fr-CA": "Besoin d'aide ?",
        "de-DE": "Hilfe gefällig?",
        "it-IT": "Serve aiuto?",
        "ja-JP": "お困りですか？",
        "ko-KR": "도움이 필요하십니까?",
        "es-MX": "¿Necesitas ayuda?",
        "pl-PL": "Potrzebujesz pomocy?",
        "ru-RU": "Нужна помощь?",
        "zh-CN": "需要帮助吗？",
        "es-ES": "¿Necesitas ayuda?",
        "zh-TW": "需要幫助嗎？",
        "tr-TR": "Yardıma mı ihtiyacın var?",
    };
    return mapping[locale] ?? result;
};

const GetVisitWebsitesLocale = (locale: string) => {
    var result = "Visit our other websites to get the answers you need, report directly to the devs, and learn more about the game and its strategies.";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Visite os nossos outros sites para obter as respostar que você precisa, reportar diretamente para os desenvolvedores e aprender mais sobre o jogo e suas estratégias.",
        "cs-CZ": "Navštivte naše další stránky a získejte odpovědi, které chcete, podejte hlášení vývojářům a zjistěte více o hře a herních strategiích.",
        "nl-NL": "Hulp nodig?", // TODO: this was in the initial files (??)
        "fr-FR": "Rendez-vous sur nos autres sites pour obtenir les réponses à vos questions, signaler une situation directement à notre équipe de développeurs, ou encore en apprendre davantage sur le jeu et ses stratégies.",
        "fr-CA": "Rendez-vous sur nos autres sites pour obtenir les réponses à vos questions, signaler une situation directement à notre équipe de développeurs, ou encore en apprendre davantage sur le jeu et ses stratégies.",
        "de-DE": "Besuche unsere anderen Websites und hol dir dort die passenden Antworten, erstatte Bericht direkt bei den Entwicklern und erfahre mehr über das Spiel und seine Strategien.",
        "it-IT": "Visita i nostri altri siti web per ottenere le risposte di cui hai bisogno, fare segnalazioni direttamente agli sviluppatori e scoprire di più sul gioco e le sue strategie.",
        "ja-JP": "UBIの他のウェブサイトにもアクセスして、質問や回答を確認し、開発チームに直接報告し、ゲームについてさらに詳しく知りましょう。",
        "ko-KR": "당사의 다른 웹사이트에 방문하여 원하는 답을 찾고, 개발진에게 바로 신고를 하고 게임과 게임 내 전략에 대해 더 자세히 알아 보십시오.",
        "es-MX": "Visita nuestras webs para encontrar las respuestas que necesitas, reportar directamente al desarrollador o aprender más cosas del juego, y más estrategias.",
        "pl-PL": "Odwiedź nasze inne strony, aby znaleźć odpowiedź na swoje pytania. Zgłaszaj problemy do twórców i dowiedz się wiecej o samej grze i dostępnych strategiach działania.",
        "ru-RU": "Ищите ответы на наших сайтах, отправляйте запросы разработчикам и узнайте больше об игре и стратегиях.",
        "zh-CN": "前往我们的其他网站，取得所需信息，直接向开发团队提出意见，并且学习更多游戏与战略知识。",
        "es-ES": "Entra en nuestras páginas web para conseguir respuestas a tus dudas, informar de algo a los desarrolladores y saber más cosas del juego y de las posibles estrategias.",
        "zh-TW": "前往我們的其他網站，取得所需資訊、直接向開發團隊提出意見，並且學習更多遊戲與戰略知識。",
        "tr-TR": "İhtiyacın olan cevapları almak için diğer internet sitelerimizi ziyaret et, doğrudan geliştiricilere bildir ve oyun ile stratejileriyle ilgili daha fazlasını öğren.",
    };

    return mapping[locale] ?? result;
};

const GetVisitWebsitesLocaleFh = (locale: string) => { // TODO
    var result = 'For any account related issues, or technical support please visit the <a href="https://support.ubisoft.com/en-US/Games/4315">Ubisoft Customer Support</a>. <br/>' +
        'For any suggestions regarding the game, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> is the best place to share with other players and our community managers. <br/>' +
        'For any bugs you are experiencing within our game, the <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> helps us understand the needs of the community.';

    var mapping: Record<string, string> = {
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": 'Para problemas relacionados à conta ou para contatar o suporte técnico, acesse <a href="https://support.ubisoft.com/pt-BR/Games/4315">Ubisoft Customer Supportt</a>. <br/>' +
            'Para enviar sugestões, o <a href="https://www.reddit.com/r/forhonor/">Reddit</a> é o melhor lugar para compartilhar opiniões com outros jogadores e nossos gerentes de comunidade. <br/>' +
            'Para bugs que tenha encontrado no jogo, o <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nos ajuda a entender as necessidades da comunidade.',
        "cs-CZ": 'Pokud máte potíže s účtem či potřebujete technickou podporu, navštivte prosím <a href="https://support.ubisoft.com/cs-CZ/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Pokud máte nějaké návrhy ohledně hry, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> je to nejlepší místo, kde se o ně můžete podělit s ostatními hráči a našimi komunitními manažery. <br/>' +
            'Pokud v naší hře narazíte na chyby a problémy, <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nám pomáhá pochopit potřeby komunity.',
        "nl-NL": 'Als je problemen hebt met je account of technische ondersteuning nodig hebt, kun je terecht bij de <a href="https://support.ubisoft.com/nl-NL/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Als je suggesties voor de game hebt, kun je ze het beste op <a href="https://www.reddit.com/r/forhonor/">Reddit</a> delen met andere spelers en onze communitymanagers. <br/>' +
            'Als je op problemen stuit in onze game, kun je via de <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> laten weten wat er speelt in de community.',
        "fr-FR": 'Pour tout problème lié à votre compte ou au support technique, rendez-vous sur <a href="https://support.ubisoft.com/fr-FR/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Pour toute suggestion liée au jeu, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> est le meilleur endroit pour échanger avec d\'autres joueurs et nos community managers. <br/>' +
            'Si vous trouvez un bug dans notre jeu, le <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nous aide à mieux comprendre les attentes de la communauté.',
        "fr-CA": 'Pour tout problème lié à votre compte ou au support technique, rendez-vous sur <a href="https://support.ubisoft.com/fr-FR/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Pour toute suggestion liée au jeu, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> est le meilleur endroit pour échanger avec d\'autres joueurs et nos community managers. <br/>' +
            'Si vous trouvez un bug dans notre jeu, le <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nous aide à mieux comprendre les attentes de la communauté.',
        "de-DE": 'Bei Problemen mit dem Konto oder technischen Anfragen besucht bitte den <a href="https://support.ubisoft.com/de-DE/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Für alle Vorschläge zum Spiel ist <a href="https://www.reddit.com/r/forhonor/">Reddit</a> der beste Ort, um eure Gedanken mit anderen Spielenden und unseren Community-Managern zu teilen. <br/>' +
            'Für alle Bugs, auf die ihr in unserem Spiel trefft, ist der <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> die richtige Anlaufstelle, um uns dabei zu helfen, die Bedürfnisse der Community zu verstehen.',
        "it-IT": 'Per problemi relativi all\'account o supporto tecnico, visita <a href="https://support.ubisoft.com/it-IT/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Per qualsiasi suggerimento riguardante il gioco, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> è il posto migliore per condividere opinioni con altri giocatori e community manager. <br/>' +
            'Se ci sono dei bug nel nostro gioco, il <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> ci permette di capire i bisogni della community.',
        "ja-JP": 'アカウント関連の不具合が発生した場合や、テクニカルサポートをご希望の場合は、<a href="https://support.ubisoft.com/ja-JP/Games/4315">Ubisoft Customer Support</a>をご確認ください。<br/>' +
            'ゲームに関するご提案がございましたら、<a href="https://www.reddit.com/r/forhonor/">Reddit</a>でプレイヤーの皆さんや弊社コミュニティマネージャーとシェアをお願いいたします。<br/>' +
            'ゲームでバグが発生した場合は、<a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a>からご報告ください。',
        "ko-KR": '계정 관련 문제 및 기술 지원은 <a href="https://support.ubisoft.com/ko-KR/Games/4315">Ubisoft Customer Support</a>을 방문해주십시오.<br/>' +
            '게임에 대한 제안이 있으시다면 <a href="https://www.reddit.com/r/forhonor/">Reddit</a>에 방문해주십시오. 다른 플레이어 및 커뮤니티 매니저와 의견을 공유하기 가장 좋은 곳입니다.<br/>' +
            '게임 내에서 버그를 발견하셨다면 <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a>를 이용해주십시오. 커뮤니티의 요구 사항을 전달하실 수 있습니다.',
        "es-MX": 'Si tienen algún problema con su cuenta o necesitan asistencia técnica, visiten el sitio web <a href="https://support.ubisoft.com/es-MX/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Si tienen alguna pregunta sobre el juego, en <a href="https://www.reddit.com/r/forhonor/">Reddit</a> podrán hablar con otros jugadores y con nuestros community managers. <br/>' +
            'Si encuentran algún error en el juego, el <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nos ayuda a entender las necesidades de la comunidad.',
        "pl-PL": 'W razie problemów związanych z kontem lub chęci uzyskania pomocy technicznej odwiedź stronę <a href="https://support.ubisoft.com/pl-PL/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Jeśli chcesz poznać sugestie dotyczące gry, <a href="https://www.reddit.com/r/forhonor/">Reddit</a> to najlepsze miejsce do dzielenia się swoimi obserwacjami z innymi graczami oraz naszymi menedżerami społeczności. <br/>' +
            'W przypadku natrafienia na jakiekolwiek błędy skorzystaj z narzędzia <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a>, które pomaga nam zrozumieć potrzeby naszej społeczności.',
        "ru-RU": 'Если у вас есть вопросы по поводу учетной записи или вам нужна техническая поддержка, обратитесь в <a href="https://support.ubisoft.com/ru-RU/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Если вы хотите поделиться своими идеями по поводу игры, лучше всего делать это на <a href="https://www.reddit.com/r/forhonor/">Reddit</a>, где ваше мнение смогут узнать другие игроки и специалисты по работе с сообществом. <br/>' +
            'Если в игре вы столкнулись с неполадкой, сообщите об этом <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> - так мы сможем узнать, над чем нужно поработать.',
        "zh-CN": '如果您有任何与账户相关的问题，或是需要技术支持，请联系 <a href="https://support.ubisoft.com/zh-CN/Games/4315">Ubisoft Customer Support</a>。<br/>' +
            '如有与游戏相关的建议，请前往 <a href="https://www.reddit.com/r/forhonor/">Reddit</a>，这是个与其他玩家和我们社区经理交流的好地方。<br/>' +
            '如果您在我们的游戏中发现错误，可以前往 <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a>，帮助我们更好地了解社区的需求。',
        "es-ES": 'Si tienes algún problema con tu cuenta o necesitas asistencia técnica, visita el sitio web <a href="https://support.ubisoft.com/es-ES/Games/4315">Ubisoft Customer Support</a>. <br/>' +
            'Si tienes alguna pregunta sobre el juego, en <a href="https://www.reddit.com/r/forhonor/">Reddit</a> podrás hablar con otros jugadores y con nuestros community managers. <br/>' +
            'Si encuentras algún error en el juego, el <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> nos ayuda a entender las necesidades de la comunidad.',
        "zh-TW": '如有任何帳戶相關問題，或是需要技術支援，請前往 <a href="https://support.ubisoft.com/zh-TW/Games/4315">Ubisoft Customer Support</a>。<br/>' +
            '如有任何與遊戲相關的建議，歡迎前往 <a href="https://www.reddit.com/r/forhonor/">Reddit</a> 與其他玩家和我們的社群經理分享。<br/>' +
            '如果在遊戲中碰到任何錯誤，請透過<a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a>來協助我們了解社群所需。',
        "tr-TR": 'Hesapla ilgili sorunlar veya teknik destek için lütfen <a href="https://support.ubisoft.com/tr-TR/Games/4315">Ubisoft Customer Support</a> adresini ziyaret edin. <br/>' +
            'Oyunla ilgili görüş ve önerilerinizi <a href="https://www.reddit.com/r/forhonor/">Reddit</a> adresinden diğer oyuncularla ve topluluk yöneticilerimizle paylaşabilirsiniz. <br/>' +
            'Oyunda karşılaştığınız hataları <a href="https://forhonor.ubisoft.com/bugreporter/en-us/">Bug Reporter</a> adresinden bize bildirerek topluluğumuzun ihtiyaçlarını anlamamıza yardımcı olabilirsiniz.',
    };
    return mapping[locale] ?? result;
};

const GetVisitWebsitesLocaleTc2 = (locale: string) => {
    var result =
        <>For technical help or account-related issues, please visit our <a href="https://support.ubisoft.com/">Customer Support</a>. For more information on The Crew®, check our <a href="https://thecrew-game.ubisoft.com/">official website</a>. Join the The Crew® community on the <a href="https://discussions.ubisoft.com/category/555/">forums</a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>, and <a href="https://discord.gg/thecrew">Discord</a>, and be sure to follow us on <a href="https://www.twitch.tv/thecrewgame">Twitch</a> to never miss a livestream. The game’s Photo Gallery, Leaderboards and Summit Calendar are all viewable on <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Videos highlighted by our in-game sharing tool can be seen on the official <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">The Crew® 2 - Community Videos channel</a>.
        </>;

    var mapping: Record<string, ReactFragment> = {
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,

        "pt-BR":
            <>Para suporte técnico ou problemas relacionados à conta, visite nosso site de <a href="https://support.ubisoft.com/">Suporte ao Cliente</a>. Para mais informações sobre The Crew®, confira <a href="https://thecrew-game.ubisoft.com/">nosso site oficial</a>. Participe da comunidade do The Crew® nos <a href="https://discussions.ubisoft.com/category/555/">fóruns</a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a> e <a href="https://discord.gg/thecrew">Discord</a>, e nos siga no <a href="https://www.twitch.tv/thecrewgame">Twitch</a> para nunca perder uma transmissão ao vivo. A Galeria de Fotos, os Placares de Líderes e o Calendário de Summits do jogo estão todos disponíveis no <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Os vídeos destacados pela nossa ferramenta de compartilhamento no jogo podem ser vistos no <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">canal oficial de vídeos da comunidade The Crew® 2</a>.
            </>,
        "nl-NL":
            <>"Als je technische hulp zoekt of problemen met je account hebt, ga dan naar de site van <a href="https://support.ubisoft.com/">onze klantenservice</a>. Ga voor meer informatie over The Crew®, naar <a href="https://thecrew-game.ubisoft.com/">onze officiële website</a>. Sluit je aan bij de community van The Crew® op het <a href="https://discussions.ubisoft.com/category/555/">forum</a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a> en <a href="https://discord.gg/thecrew">Discord</a> , en volg ons op <a href="https://www.twitch.tv/thecrewgame">Twitch</a>, zodat je nooit een livestream mist. De Fotogalerie, Klassementen en Summit-kalender van de game zijn allemaal te bekijken in de <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Video's die worden uitgelicht door onze deeltool kun je zien op het <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">officiële kanaal met communityvideo's van The Crew® 2</a>.
            </>,
        "fr-FR":
            <>Pour une assistance technique ou si vous rencontrez des problèmes de compte, veuillez vous rendre sur notre <a href="https://support.ubisoft.com/">site d'assistance</a>. Pour plus d'informations sur The Crew®, rendez-vous sur <a href="https://thecrew-game.ubisoft.com/">notre site officiel</a>. Rejoignez la communauté The Crew® sur <a href="https://discussions.ubisoft.com/category/555/">les forums</a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>, et <a href="https://discord.gg/thecrew">Discord</a>, et n'oubliez pas de nous suivre sur <a href="https://www.twitch.tv/thecrewgame">Twitch</a> pour ne jamais rater un stream. Toutes les photos partagées sur notre Galerie, les classements et le calendrier des Summits sont consultables sur <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Toutes les vidéos sélectionnées par notre outil de partage intégré sont consultables sur <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">la chaîne The Crew® 2 - Community Videos officielle</a>.
            </>,
        "fr-CA":
            <>Pour une assistance technique ou si vous rencontrez des problèmes de compte, veuillez vous rendre sur notre <a href="https://support.ubisoft.com/">site d'assistance</a>. Pour plus d'informations sur The Crew®, rendez-vous sur <a href="https://thecrew-game.ubisoft.com/">notre site officiel</a>. Rejoignez la communauté The Crew® sur <a href="https://discussions.ubisoft.com/category/555/">les forums</a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>, et <a href="https://discord.gg/thecrew">Discord</a>, et n'oubliez pas de nous suivre sur <a href="https://www.twitch.tv/thecrewgame">Twitch</a> pour ne jamais rater un stream. Toutes les photos partagées sur notre Galerie, les classements et le calendrier des Summits sont consultables sur <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Toutes les vidéos sélectionnées par notre outil de partage intégré sont consultables sur <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">la chaîne The Crew® 2 - Community Videos officielle</a>.
            </>,
        "de-DE":
            <>Wenn du technische Hilfe benötigst oder Probleme mit dem Konto hast, besuche bitte <a href="https://support.ubisoft.com/">unsere Kundenservice-Website</a>. Besuche für weitere Informationen zu The Crew® <a href="https://thecrew-game.ubisoft.com/">unsere offizielle Website</a>. Schließ dich der The Crew® Community im <a href="https://discussions.ubisoft.com/category/555/">Forum</a>, auf <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>, und <a href="https://discord.gg/thecrew">Discord an und folge uns auf <a href="https://www.twitch.tv/thecrewgame">Twitch</a>, um keinen Livestream zu verpassen. Die spielinterne Fotogalerie, Bestenlisten und der Summit-Kalender sind im <a href="https://www.thecrew-hub.com">The Crew® Hub</a> zu finden. Videos, die von unserem spielinternen Teilen-Werkzeug hervorgehoben werden, findest du auf dem <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">
            </a>offiziellen The Crew® 2-Community-Videokanal</a>.
            </>,
        "it-IT":
            <>In caso di problemi tecnici o dell'account, visita il <a href="https://support.ubisoft.com/">sito del supporto clienti</a>. Per maggiori informazioni su The Crew® visita il nostro sito ufficiale. Unisciti alla community di The Crew® <a href="https://discussions.ubisoft.com/category/555/">sui forum</a>, su <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, su <a href="https://www.facebook.com/groups/TheCrewSocial/">The Crew Social</a> e su <a href="https://discord.gg/thecrew">Discord</a> e seguici su <a href="https://www.twitch.tv/thecrewgame">Twitch</a> per non perdere nessuno streaming live. La galleria fotografica del gioco, le classifiche e il calendario dei Summit sono disponibili su <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. I video in evidenza sul nostro sistema di condivisione in-game sono disponibili sul canale ufficiale della community di <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">The Crew® 2</a>.
            </>,
        "ja-JP":
            <>技術的なサポートが必要な場合やアカウントに関する問題でお困りの場合は、<a href="https://support.ubisoft.com/">カスタマーサポートサイト</a>で解決方法などをご参照いただけます。「The Crew®」の詳細は、<a href="https://thecrew-game.ubisoft.com/">公式ウェブサイト</a>でご確認ください。<a href="https://discussions.ubisoft.com/category/555/">フォーラム</a>、<a href="https://www.reddit.com/r/The_Crew/">Reddit</a>、<a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>、<a href="https://discord.gg/thecrew">Discord</a>で「The Crew®」コミュニティに参加し、ライブストリームを見逃さないように、<a href="https://www.twitch.tv/thecrewgame">Twitch</a> で公式アカウントをフォローしましょう。ゲームのフォトギャラリー、ランキング、SUMMITカレンダーはすべて<a href="https://www.thecrew-hub.com">The Crew® Hub</a>でご覧いただけます。ゲーム内のシェアツールで注目を集めた動画はすべて、公式チャンネル<a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">The Crew® 2 - Community Videos channel</a>で公開中です。
            </>,
        "ko-KR":
            <>기술적 도움이 필요하거나 계정 관련 문제가 있다면 <a href="https://support.ubisoft.com/">고객 지원 사이트</a> 에 방문해 주십시오. <a href="https://thecrew-game.ubisoft.com/">The Crew®에 대한 더 자세한 내용은 공식 웹사이트</a>에서 확인하십시오. 포럼과 <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a> 및 <a href="https://discord.gg/thecrew">Discord</a>에서 The Crew® 커뮤니티에 참여하고 <a href="https://www.twitch.tv/thecrewgame">Twitch</a>에서 저희를 팔로우해 라이브 방송을 놓치지 마십시오. 게임의 사진 갤러리, 순위표, Summit 달력은 <a href="https://www.thecrew-hub.com">The Crew® Hub</a>에서 보실 수 있습니다. 저희의 인게임 공유 툴로 하이라이트되는 모든 비디오는 공식 <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">The Crew® 2 - 커뮤니티 비디오 채널</a>에서 보실 수 있습니다.
            </>,
        "es-MX":
            <>Si necesitas asistencia técnica o tienes problemas con tu cuenta, visita <a href="https://support.ubisoft.com/">el Sitio web de asistencia al cliente</a>. Para obtener más información sobre The Crew®, visita nuestro <a href="https://thecrew-game.ubisoft.com/">sitio web oficial</a>. Únete a la comunidad de The Crew® en <a href="https://discussions.ubisoft.com/category/555/">los foros </a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a> y <a href="https://discord.gg/thecrew">Discord</a> y recuerda seguirnos en <a href="https://www.twitch.tv/thecrewgame">Twitch</a> para no perderte ninguna transmisión en directo. La galería fotográfica del juego, los marcadores y el calendario de Summit están disponibles en <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Los vídeos destacados en nuestra herramienta de vídeos del juego se pueden ver en <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">el canal oficial de vídeos de la comunidad de The Crew® 2</a>.
            </>,
        "pl-PL":
            <>Jeśli potrzebujesz pomocy technicznej lub związanej ze swoim kontem, odwiedź naszą <a href="https://support.ubisoft.com/">stronę wsparcia klienta</a>. Jeśli szukasz więcej informacji na temat gry The Crew®, zajrzyj na naszą <a href="https://thecrew-game.ubisoft.com/">oficjalną witrynę</a>. Dołącz do społeczności The Crew® na <a href="https://discussions.ubisoft.com/category/555/">forach</a> oraz w serwisie <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a>, a także na serwerze <a href="https://discord.gg/thecrew">Discord</a> i pamiętaj, żeby obserwować nas na serwisie <a href="https://www.twitch.tv/thecrewgame">Twitch</a>, dzięki czemu nie ominie cię żadna transmisja na żywo. Naszą galerię, rankingi oraz kalendarz wydarzeń Summit można przeglądać w <a href="https://www.thecrew-hub.com">The Crew® Hub</a>.Filmy wybrane przez nasze narzędzie wbudowane w grę, przeznaczone do dzielenia się zawartością, można zobaczyć na <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">oficjalnym kanale z filmami społeczności The Crew® 2</a>.
            </>,
        "ru-RU":
            <>По техническим вопросам и в случае возникновения затруднений с учетной записью обратитесь в нашу <a href="https://support.ubisoft.com/">службу поддержки</a> . Чтобы узнать больше о The Crew® 2, посетите наш <a href="https://thecrew-game.ubisoft.com/">официальный сайт</a> . Присоединяйтесь к сообществу The Crew® на <a href="https://discussions.ubisoft.com/category/555/">форумах</a> , на <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, в <a href="https://www.facebook.com/groups/TheCrewSocial/">The Crew Social</a> и в <a href="https://discord.gg/thecrew">Discord</a>. И не пропускайте наши трансляции на <a href="https://www.twitch.tv/thecrewgame">Twitch</a>. Галерея снимков из игры, списки лидеров и календарь Summit доступны на сайте <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Все видео, отмеченные с помощью внутриигрового инструмента для публикации, можно найти на <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">официальном видеоканале сообщества The Crew® 2</a>.

            </>,
        "zh-CN":
            <>如需技術說明或有帳戶相關問題，<a href="https://support.ubisoft.com/">請前往我們的客戶服務網站</a>。如需<a href="https://thecrew-game.ubisoft.com/">《The Crew®》</a>的更多相關資訊，請參閱我們的官方網站。加入《The Crew®》<a href="https://discussions.ubisoft.com/category/555/">社群討論區</a>、<a href="https://www.reddit.com/r/The_Crew/">Reddit </a>、The Crew Social <a href="https://www.facebook.com/groups/TheCrewSocial/">社團</a> 、<a href="https://discord.gg/thecrew">及 Discord </a>，並記得追蹤我們的 <a href="https://www.twitch.tv/thecrewgame">Twitch</a> 才不會錯過任何直播。遊戲的照片藝廊、排行榜和高峰賽行事曆都可以在 <a href="https://www.thecrew-hub.com">The Crew® Hub</a> 查看。透過遊戲內分享工具上傳的精彩影片則會出現在官方<a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">《The Crew® 2》</a>社群影片頻道。
            </>,
        "es-ES":
            <>Si necesitas asistencia técnica o tienes problemas con tu cuenta, visita <a href="https://support.ubisoft.com/">el Sitio web de asistencia al cliente</a>. Para obtener más información sobre The Crew®, visita nuestro <a href="https://thecrew-game.ubisoft.com/">sitio web oficial</a>. Únete a la comunidad de The Crew® en <a href="https://discussions.ubisoft.com/category/555/">los foros </a>, <a href="https://www.reddit.com/r/The_Crew/">Reddit</a>, <a href="https://www.facebook.com/groups/TheCrewSocial/">TC Social</a> y <a href="https://discord.gg/thecrew">Discord</a> y recuerda seguirnos en <a href="https://www.twitch.tv/thecrewgame">Twitch</a> para no perderte ninguna transmisión en directo. La galería fotográfica del juego, los marcadores y el calendario de Summit están disponibles en <a href="https://www.thecrew-hub.com">The Crew® Hub</a>. Los vídeos destacados en nuestra herramienta de vídeos del juego se pueden ver en <a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">el canal oficial de vídeos de la comunidad de The Crew® 2</a>.
            </>,
        "zh-TW":
            <>如需技術說明或有帳戶相關問題，<a href="https://support.ubisoft.com/">請前往我們的客戶服務網站</a>。如需<a href="https://thecrew-game.ubisoft.com/">《The Crew®》</a>的更多相關資訊，請參閱我們的官方網站。加入《The Crew®》<a href="https://discussions.ubisoft.com/category/555/">社群討論區</a>、<a href="https://www.reddit.com/r/The_Crew/">Reddit </a>、The Crew Social <a href="https://www.facebook.com/groups/TheCrewSocial/">社團</a> 、<a href="https://discord.gg/thecrew">及 Discord </a>，並記得追蹤我們的 <a href="https://www.twitch.tv/thecrewgame">Twitch</a> 才不會錯過任何直播。遊戲的照片藝廊、排行榜和高峰賽行事曆都可以在 <a href="https://www.thecrew-hub.com">The Crew® Hub</a> 查看。透過遊戲內分享工具上傳的精彩影片則會出現在官方<a href="https://www.youtube.com/channel/UCvsr9fskyDuzXwfdAAJDVBA">《The Crew® 2》</a>社群影片頻道。
            </>
    }

    return mapping[locale] ?? result;
}

const GetMaintenanceTitlebarLocale = (locale: string) => {
    var result = "Maintenance";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "zh-CN": "维护时程",
        "zh-TW": "維修時程",
        "cs-CZ": "Rozpis údržby",
        "nl-NL": "Onderhoudsschema",
        "fr-FR": "Planning de maintenance",
        "fr-CA": "Planning de maintenance",
        "de-DE": "Wartungs-Zeitplan",
        "it-IT": "Programma di manutenzione",
        "ja-JP": "メンテナンススケジュール",
        "ko-KR": "유지 관리 일정",
        "pl-PL": "Harmonogram konserwacji",
        "pt-BR": "Programação de manutenção",
        "ru-RU": "Плановое техобслуживание",
        "es-ES": "Horario de mantenimiento",
        "es-MX": "Horario de mantenimiento",
    };
    return mapping[locale] ?? result;
};

const GetTechnicalSupportForumLocale = (locale: string) => {
    var result = "To participate in on-going discussions, please review our technical support forum.";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "zh-CN": "若想参与正在进行的讨论，请浏览我们的技术客服论坛。",
        "zh-TW": "若要參與正在進行的討論，請瀏覽技術支援論壇。",
        "cs-CZ": "Dlouhodobé diskuse se můžeš zúčastnit na fórech naší technické podpory.",
        "nl-NL": "Ga naar ons technische-ondersteuningsforum om deel te nemen aan lopende discussies.",
        "fr-FR": "Pour participer aux discussions en temps réel, consultez notre forum de support technique.",
        "fr-CA": "Pour participer aux discussions en temps réel, consultez notre forum de support technique.",
        "de-DE": "Um an laufenden Diskussionen teilzunehmen, sieh dir bitte unser Technisches Support-Forum an.",
        "it-IT": "Per partecipare alle discussioni in corso, visita il forum del nostro supporto tecnico.",
        "ja-JP": "現在進行中の議論へ参加するには、テクニカルサポートフォーラムをご確認ください。",
        "ko-KR": "진행 중인 토론에 참여하려면 기술 지원 포럼을 확인하세요.",
        "pl-PL": "Aby wziąć udział w trwających dyskusjach, sprawdź forum pomocy technicznej.",
        "pt-BR": "Para participar das discussões em andamento, confira nosso fórum do suporte técnico.",
        "ru-RU": "Для участия в обсуждениях загляните на форум техподдержки.",
        "es-ES": "Para participar en las conversaciones existentes, echa un vistazo a nuestro foro de soporte técnico.",
        "es-MX": "Para participar en las conversaciones existentes, echa un vistazo a nuestro foro de soporte técnico.",
    };
    return mapping[locale] ?? result;
};

const GetNetworkServiceIssuesLocale = (locale: string) => {
    var result = "Current network or service issues.";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "zh-CN": "当前网络或服务问题。",
        "zh-TW": "目前連線或服務問題。",
        "cs-CZ": "Stávající potíže se sítí či službami.",
        "nl-NL": "Huidige netwerk- of serviceproblemen",
        "fr-FR": "Problème de réseau ou de service en cours.",
        "fr-CA": "Problème de réseau ou de service en cours.",
        "de-DE": "Aktuelle Netzwerk- oder Serverprobleme.",
        "it-IT": "Problemi di rete o di servizio in corso.",
        "ja-JP": "現在のネットワーク/サービスの問題。",
        "ko-KR": "현재 네트워크나 서비스 문제.",
        "pl-PL": "Aktualne problemy z usługami lub siecią.",
        "pt-BR": "Problemas atuais de rede ou serviços.",
        "ru-RU": "Известные неполадки в работе сервисов или сети.",
        "es-ES": "Problemas en el servicio o en la red.",
        "es-MX": "Problemas en el servicio o en la red.",
    };
    return mapping[locale] ?? result;
};

const GetNoNewsMessage = (localeCode: string) => {
    var result = "There are currently no issues or updates to report.";

    var mapping: Record<string, string> = {
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "No momento, não há problemas ou atualizações para reportar.",
        "cs-CZ": "Aktuálně nejsou k nahlášení žádné problémy nebo aktualizace.",
        "nl-NL": "Er zijn momenteel geen problemen of updates.",
        "fr-FR": "Aucun problème ou mise à jour à signaler actuellement.",
        "fr-CA": "Aucun problème ou mise à jour à signaler actuellement.",
        "de-DE": "Momentan gibt es keine Probleme oder Aktualisierungen.",
        "it-IT": "Non ci sono problemi o aggiornamenti da segnalare.",
        "ja-JP": "報告する問題や更新内容は現在ありません。",
        "ko-KR": "현재 보고할 문제 또는 업데이트가 없습니다.",
        "es-MX": "No hay ahora problemas o actualizaciones que reportar.",
        "pl-PL": "Obecnie nie ma żadnych problemów ani aktualizacji.",
        "ru-RU": "Неполадки или обновления отсутствуют.",
        "zh-CN": "目前没有任何问题或更新通知。",
        "es-ES": "No hay problemas ni actualizaciones de los que informar.",
        "zh-TW": "目前沒有任何問題或更新通知。",
        "tr-TR": "Şu anda rapor edilecek bir sorun veya güncelleme yok.",
        "ar-AR": "ما من مشاكل أو تحديثات للإبلاغ بها حاليا",
        "th-TH": "ยังไม่พบปัญหาหรืออัพเดทเพื่อที่จะรีพอร์ต",
    };

    return mapping[localeCode] ?? result;
};

const GetLegendOperationalLocale = (locale: string) => {
    var result = "No Issues";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Nenhum problema",
        "cs-CZ": "Žádné problémy",
        "nl-NL": "Geen problemen",
        "fr-FR": "Aucun problème",
        "fr-CA": "Aucun problème",
        "de-DE": "Keine Probleme",
        "it-IT": "Nessun problema",
        "ja-JP": "問題なし",
        "ko-KR": "이상 없다",
        "es-MX": "Sin problema",
        "pl-PL": "Brak problemów",
        "ru-RU": "Нет неполадок",
        "zh-CN": "正常运作",
        "es-ES": "Sin incidencias",
        "tr-TR": "Sorun Yok",
        "ar-AR": "لا مشاكل",
        "th-TH": "ไม่มีปัญหา",
        "zh-TW": "正常運作",
    };

    return mapping[locale] ?? result;
};

const GetLegendMaintenanceLocale = (locale: string) => {
    var result = "Maintenance";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Manutenção planejada",
        "cs-CZ": "Plánovaná údržba",
        "nl-NL": "Gepland onderhoud",
        "fr-FR": "Maintenance prévue",
        "fr-CA": "Maintenance prévue",
        "de-DE": "Geplante Wartung",
        "it-IT": "Manutenzione pianificata",
        "ja-JP": "計画メンテナンス",
        "ko-KR": "정기 점검",
        "es-MX": "Mantenimiento programado",
        "pl-PL": "Planowana przerwa techniczna",
        "ru-RU": "Запланированные тех. работы",
        "zh-CN": "例行维护",
        "es-ES": "Mantenimiento programado",
        "tr-TR": "Planlanmış bakım",
        "ar-AR": "صيانة مخطط لها",
        "th-TH": "การซ่อมบำรุงตามกำหนด",
        "zh-TW": "例行維護",
    };

    return mapping[locale] ?? result;
};

const GetLegendProblemsLocale = (locale: string) => {
    var result = "Unplanned Issues";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Questões não planejadas",
        "cs-CZ": "Neplánované problémy",
        "nl-NL": "Onvoorziene problemen",
        "fr-FR": "Problèmes inattendus",
        "fr-CA": "Problèmes inattendus",
        "de-DE": "Unerwartete Probleme",
        "it-IT": "Problemi inaspettati",
        "ja-JP": "想定外の問題",
        "ko-KR": "불시 발생 문제",
        "es-MX": "Problemas no planificados",
        "pl-PL": "Nieplanowane problemy",
        "ru-RU": "Возникли неполадки",
        "zh-CN": "预期外错误",
        "es-ES": "Incidencias imprevistas",
        "tr-TR": "Planlanmamış Sorunlar",
        "ar-AR": "مشاكل غير متوقعة",
        "th-TH": "ปัญหาที่ไม่ได้คาดคิด",
        "zh-TW": "預期外錯誤",
    };

    return mapping[locale] ?? result;
};

const GetLegendOutageLocale = (locale: string) => {
    var result = "Unplanned Outage";

    var mapping: Record<string, string> = {
        "en-US": result,
        "en-GB": result,
        "en-CA": result,
        "en-AU": result,
        "pt-BR": "Interrupção não planejada",
        "cs-CZ": "Neplánovaný výpadek",
        "nl-NL": "Onvoorziene uitval",
        "fr-FR": "Panne inattendue",
        "fr-CA": "Panne inattendue",
        "de-DE": "Unerwarteter Ausfall",
        "it-IT": "Interruzione imprevista",
        "ja-JP": "想定外の機能停止",
        "ko-KR": "불시 발생 정전",
        "es-MX": "Cierre no planificado",
        "pl-PL": "Nieplanowana awaria",
        "ru-RU": "Внеплановое отключение",
        "zh-CN": "预期外停机",
        "es-ES": "Cierre no programado",
        "tr-TR": "Planlanmamış Kesinti",
        "ar-AR": "انقطاع غير متوقع",
        "th-TH": "ไฟดับฉุกเฉิน",
        "zh-TW": "預期外停機",
    };

    return mapping[locale] ?? result;
};

// --

const GetGlobalStatusLocale = (locale: string) => {
    var result = "Global Status";

    var mapping: Record<string, string> = {
        "zh-CN": "全局状态",
        "zh-TW": "整體狀態",
        "cs-CZ": "Celkový stav",
        "nl-NL": "Algemene status",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "État global",
        "fr-CA": "État global",
        "de-DE": "Weltweiter Status",
        "it-IT": "Status globale",
        "ja-JP": "グローバルステータス",
        "ko-KR": "전체 상황",
        "pl-PL": "Status ogólny",
        "pt-BR": "Status Global",
        "ru-RU": "Общий статус",
        "es-ES": "Estado global",
        "es-MX": "Estado global",
        "tr-TR": "Genel Durum"
    };

    return mapping[locale] ?? result;
};

const GetExpandPlatformsLocale = (locale: string) => {
    var result = "Expand all";

    var mapping: Record<string, string> = {
        "zh-CN": "全部展开",
        "zh-TW": "全部展開",
        "cs-CZ": "Rozbalit vše",
        "nl-NL": "Alles uitvouwen",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Afficher tout",
        "fr-CA": "Afficher tout",
        "de-DE": "Alle ausklappen",
        "it-IT": "Espandi tutto",
        "ja-JP": "すべて表示",
        "ko-KR": "모두 펼치기",
        "pl-PL": "Rozwiń wszystko",
        "pt-BR": "Expandir todos",
        "ru-RU": "Показать все",
        "es-ES": "Expandir todo",
        "es-MX": "Expandir todo",
        "tr-TR": "Tümünü genişlet"
    };

    return mapping[locale] ?? result;
};

const GetCollapsePlatformsLocale = (locale: string) => {
    var result = "Collapse all";

    var mapping: Record<string, string> = {
        "zh-CN": "收起全部",
        "zh-TW": "收起全部",
        "cs-CZ": "Sbalit vše",
        "nl-NL": "Alles inklappen",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Réduire tout",
        "fr-CA": "Réduire tout",
        "de-DE": "Alle zusammenfalten",
        "it-IT": "Riduci tutto",
        "ja-JP": "すべて折りたたむ",
        "ko-KR": "전부 숨기기",
        "pl-PL": "Zwiń",
        "pt-BR": "Reduzir tudo",
        "ru-RU": "Свернуть все",
        "es-ES": "Contraer todo",
        "es-MX": "Contraer todo",
        "tr-TR": "Tümünü daralt"
    };

    return mapping[locale] ?? result;
};

const GetUpdatedLocale = (locale: string) => {
    var result = "Updated";

    var mapping: Record<string, string> = {
        "zh-CN": "更新",
        "zh-TW": "更新",
        "cs-CZ": "Aktualizováno",
        "nl-NL": "Opdateret",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Actualisé",
        "fr-CA": "Actualisé",
        "de-DE": "Aktualisiert",
        "it-IT": "Aggiornato",
        "ja-JP": "更新しました",
        "ko-KR": "업데이트",
        "pl-PL": "Zaktualizowano",
        "pt-BR": "Atualizado",
        "ru-RU": "обновленный",
        "es-ES": "Actualizado",
        "es-MX": "Actualizado",
        "tr-TR": "Güncellenmiş"
    };

    return mapping[locale] ?? result;
};

const GetRefreshLocale = (locale: string) => {
    var result = "Refresh";

    var mapping: Record<string, string> = {
        "zh-CN": "刷新",
        "zh-TW": "重新整理",
        "cs-CZ": "Obnovit",
        "nl-NL": "Vernieuwen",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Rafraichir",
        "fr-CA": "Rafraichir",
        "de-DE": "Aktualisieren",
        "it-IT": "Aggiorna",
        "ja-JP": "更新",
        "ko-KR": "새로고침",
        "pl-PL": "Odśwież",
        "pt-BR": "Atualizar",
        "ru-RU": "Обновить",
        "es-ES": "Actualizar",
        "es-MX": "Actualizar",
        "tr-TR": "Yenile"
    };

    return mapping[locale] ?? result;
};

const GetConnectivitySubstatusLocale = (locale: string) => {
    var result = "Connectivity";

    var mapping: Record<string, string> = {
        "zh-CN": "连接",
        "zh-TW": "連線能力",
        "cs-CZ": "Možnost připojení",
        "nl-NL": "Verbinding",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Connectivité",
        "fr-CA": "Connectivité",
        "de-DE": "Verbindung",
        "it-IT": "Connettività",
        "ja-JP": "接続状況",
        "ko-KR": "연결",
        "pl-PL": "Połączenie",
        "pt-BR": "Conectividade",
        "ru-RU": "Возможность подключения",
        "es-ES": "Conectividad",
        "es-MX": "Conectividad",
        "tr-TR": "Baglanabilirlik"
    };

    return mapping[locale] ?? result;
};

const GetMatchmakingSubstatusLocale = (locale: string) => {
    var result = "Matchmaking";

    var mapping: Record<string, string> = {
        "zh-CN": "匹配",
        "zh-TW": "配對",
        "cs-CZ": "Spojování hráčů",
        "nl-NL": "Matchmaking",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Matchmaking",
        "fr-CA": "Matchmaking",
        "de-DE": "Spielersuche",
        "it-IT": "Matchmaking",
        "ja-JP": "マッチメイキング",
        "ko-KR": "매치메이킹",
        "pl-PL": "Dobieranie graczy",
        "pt-BR": "Criação de partidas",
        "ru-RU": "Подбор игроков",
        "es-ES": "Matchmaking",
        "es-MX": "Matchmaking",
        "tr-TR": "Eşleştirme"
    };

    return mapping[locale] ?? result;
};

const GetLeaderboardSubstatusLocale = (locale: string) => {
    var result = "Leaderboard";

    var mapping: Record<string, string> = {
        "zh-CN": "排行榜",
        "zh-TW": "排行榜",
        "cs-CZ": "Žebříček",
        "nl-NL": "Klassement",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Classement",
        "fr-CA": "Classement",
        "de-DE": "Bestenliste",
        "it-IT": "Classifiche",
        "ja-JP": "ランキング",
        "ko-KR": "순위표",
        "pl-PL": "Tabele wyników",
        "pt-BR": "Placar de líderes",
        "ru-RU": "Списки лидеров",
        "es-ES": "Clasificación",
        "es-MX": "Clasificación",
        "tr-TR": "Liderlik tablosu"
    };

    return mapping[locale] ?? result;
};

const GetStoreSubstatusLocale = (locale: string) => {
    var result = "In-Game Store";

    var mapping: Record<string, string> = {
        "zh-CN": "游戏内商店",
        "zh-TW": "遊戲內商店",
        "cs-CZ": "Herní obchod",
        "nl-NL": "In-game winkel",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Boutique en jeu",
        "fr-CA": "Boutique en jeu",
        "de-DE": "Shop im Spiel",
        "it-IT": "Negozio di gioco",
        "ja-JP": "ゲーム内ストア",
        "ko-KR": "게임 내 상점",
        "pl-PL": "Sklep w grze",
        "pt-BR": "Loja no jogo",
        "ru-RU": "Внутриигровой магазин",
        "es-ES": "Tienda del juego",
        "es-MX": "Tienda del juego",
        "tr-TR": "Oyun İçi Mağaza"
    };

    return mapping[locale] ?? result;
};

const GetAuthSubstatusLocale = (locale: string) => {
    var result = "Authentication";

    var mapping: Record<string, string> = {
        "zh-CN": "认证",
        "zh-TW": "驗證",
        "cs-CZ": "Ověřování",
        "nl-NL": "Verificatie",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Authentification",
        "fr-CA": "Authentification",
        "de-DE": "Authentifizierung",
        "it-IT": "Autenticazione",
        "ja-JP": "認証システム",
        "ko-KR": "인증",
        "pl-PL": "Weryfikacja",
        "pt-BR": "Autenticação",
        "ru-RU": "Аутентификация",
        "es-ES": "Autenticación",
        "es-MX": "Autenticación",
        "tr-TR": "Doğrulama"
    };

    return mapping[locale] ?? result;
};

const GetSubStatusTextOnlineLocale = (locale: string) => {
    var result = "Operational";

    var mapping: Record<string, string> = {
        "zh-CN": "运行",
        "zh-TW": "正常運作",
        "cs-CZ": "Funkční",
        "nl-NL": "Operationeel",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Opérationnel",
        "fr-CA": "Opérationnel",
        "de-DE": "In Funktion",
        "it-IT": "Operativo",
        "ja-JP": "通常",
        "ko-KR": "정상",
        "pl-PL": "Działa",
        "pt-BR": "Operacional",
        "ru-RU": "Доступно",
        "es-ES": "Operativo",
        "es-MX": "Operativo",
        "tr-TR": "Çalışıyor"
    };

    return mapping[locale] ?? result;
};

const GetSubStatusTextDegradedLocale = (locale: string) => {
    var result = "Degraded";

    var mapping: Record<string, string> = {
        "zh-CN": "降级",
        "zh-TW": "效能低落",
        "cs-CZ": "Omezený provoz",
        "nl-NL": "Aangetast",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Dégradé",
        "fr-CA": "Dégradé",
        "de-DE": "Beeinträchtigt",
        "it-IT": "Deteriorato",
        "ja-JP": "停止中",
        "ko-KR": "저하됨",
        "pl-PL": "Zmniejszona wydajność",
        "pt-BR": "Degradado",
        "ru-RU": "Нет доступа",
        "es-ES": "Degradado",
        "es-MX": "Degradado",
        "tr-TR": "Sınırlı"
    };

    return mapping[locale] ?? result;
};

const GetDefaultMessageWhenIssuesLocale = (locale: string) => {
    var result = "Some issues are being investigated. Thank you for your patience.";

    var mapping: Record<string, string> = {
        "zh-CN": "我们正在对部分问题进行调查。感谢您的耐心等待。",
        "zh-TW": "我們正在檢查某些問題，感謝您耐心等候！",
        "cs-CZ": "Prověřujeme jisté problémy. Děkujeme za vaši trpělivost.",
        "nl-NL": "Er worden wat problemen onderzocht. Bedankt voor je geduld.",
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result,
        "fr-FR": "Nous examinons plusieurs problèmes. Merci pour votre patience.",
        "fr-CA": "Nous examinons plusieurs problèmes. Merci pour votre patience.",
        "de-DE": "Einige Probleme werden gerade untersucht. Vielen Dank für deine Geduld.",
        "it-IT": "Stiamo analizzando qualche problema. Grazie per la pazienza.",
        "ja-JP": "問題について現在調査中です。お待たせして申し訳ありません。",
        "ko-KR": "문제가 발생해 확인 중입니다. 기다려주셔서 감사합니다.",
        "pl-PL": "Badamy pewne problemy. Dziękujemy za cierpliwość.",
        "pt-BR": "Alguns problemas estão sendo investigados. Agradecemos sua paciência.",
        "ru-RU": "Обнаружены неполадки, причина устанавливается. Благодарим за терпение.",
        "es-ES": "Estamos investigando algunos problemas. Gracias por tu paciencia.",
        "es-MX": "Estamos investigando algunos problemas. Gracias por tu paciencia.",
        "tr-TR": "Bazı sorunlar inceleniyor. Sabrınız için teşekkürler."
    };

    return mapping[locale] ?? result;
};

const GetGameStatusLocaleSppd = (locale: string) => {
    var result = "Game status";

    var mapping: Record<string, string> = {
        "en-GB": result,
        "en-US": result,
        "en-CA": result,
        "en-AU": result
    };

    return mapping[locale] ?? result;
};

const GetInstagramUrlLocale = (locale: string) => {
    var result = "";

    switch (locale) {
        case "en-US":
            result = "https://www.instagram.com/rainbow6game_us/";
            break;
        case "en-CA":
            result = "https://www.instagram.com/ubisoftesports/";
            break;
        case "fr-CA":
            result = "https://www.instagram.com/ubisoftesports/";
            break;
        case "es-MX":
            result = "https://www.instagram.com/ubisoftlatam/";
            break;
        case "pt-BR":
            result = "https://www.instagram.com/ubisoftesports/";
            break;
        case "fr-FR":
            result = "https://www.instagram.com/rainbowsix_fr/";
            break;
        default:
    }

    return result;
};

const localization = {
    supportedLocales,
    GetOutageTitlebarLocale,
    GetLiveStatusLocale,
    GetServiceStatusLocale,
    GetNeedHelpLocale,
    GetVisitWebsitesLocale,
    GetVisitWebsitesLocaleFh,
    GetVisitWebsitesLocaleTc2,
    GetMaintenanceTitlebarLocale,
    GetTechnicalSupportForumLocale,
    GetNetworkServiceIssuesLocale,
    GetInstagramUrlLocale,
    GetNoNewsMessage,
    GetLegendOperationalLocale,
    GetLegendMaintenanceLocale,
    GetLegendProblemsLocale,
    GetLegendOutageLocale,
    // --
    GetGlobalStatusLocale,
    GetExpandPlatformsLocale,
    GetCollapsePlatformsLocale: GetCollapsePlatformsLocale,
    GetRefreshLocale,
    GetUpdatedLocale,
    GetConnectivitySubstatusLocale,
    GetMatchmakingSubstatusLocale,
    GetLeaderboardSubstatusLocale,
    GetStoreSubstatusLocale,
    GetAuthSubstatusLocale,
    GetSubStatusTextOnlineLocale,
    GetSubStatusTextDegradedLocale,
    GetDefaultMessageWhenIssuesLocale,
    GetGameStatusLocaleSppd
};

export default localization;